import React, { useState, useEffect } from 'react';
import { t } from '../i18n';

const ZODIAC_DATA = {
  Aries: {
    emoji: '🐏', dates: 'Mar 21 - Apr 19', element: '🔥 Fire', planet: '♂️ Mars',
    stone: '💎 Diamond', color: '❤️ Red', lucky: [1,8,17], traits: ['Courageous','Energetic','Confident','Impulsive','Adventurous'],
    desc: 'Aries is the first sign of the zodiac. Bold and ambitious, Aries dives headfirst into challenging situations.',
    love: 'Passionate and direct in love. Best matches: Leo, Sagittarius, Gemini.',
    career: 'Natural leaders. Great for entrepreneurship, military, sports, and management.',
    health: 'Prone to headaches and stress. Regular exercise helps channel excess energy.',
    today: 'Your energy is at peak today. Take initiative and start new projects. Financial opportunities await!',
  },
  Taurus: {
    emoji: '🐂', dates: 'Apr 20 - May 20', element: '🌍 Earth', planet: '♀️ Venus',
    stone: '💚 Emerald', color: '💚 Green', lucky: [2,6,9], traits: ['Reliable','Patient','Practical','Stubborn','Devoted'],
    desc: 'Taurus is an earth sign represented by the bull. Taureans enjoy relaxing in serene environments.',
    love: 'Loyal and sensual in love. Best matches: Virgo, Capricorn, Cancer.',
    career: 'Excel in finance, art, cooking, and architecture.',
    health: 'Watch out for throat issues. Enjoy nature walks and relaxation.',
    today: 'Focus on financial matters today. A good day for long-term investments and home improvements.',
  },
  Gemini: {
    emoji: '👯', dates: 'May 21 - Jun 20', element: '💨 Air', planet: '☿️ Mercury',
    stone: '💛 Agate', color: '💛 Yellow', lucky: [3,12,23], traits: ['Curious','Adaptable','Witty','Inconsistent','Communicative'],
    desc: 'Gemini is a versatile and curious sign. Represented by twins, Geminis have two sides to their personality.',
    love: 'Playful and intellectual in love. Best matches: Libra, Aquarius, Aries.',
    career: 'Great communicators. Excel in journalism, teaching, sales, and writing.',
    health: 'Prone to nervous tension. Meditation and breathing exercises help.',
    today: 'Communication is your superpower today. Network and socialize — great connections await!',
  },
  Cancer: {
    emoji: '🦀', dates: 'Jun 21 - Jul 22', element: '💧 Water', planet: '🌙 Moon',
    stone: '🤍 Pearl', color: '⚪ Silver', lucky: [2,7,11], traits: ['Intuitive','Emotional','Protective','Moody','Loyal'],
    desc: 'Cancer is the cardinal water sign. Represented by the crab, Cancers are deeply intuitive and sentimental.',
    love: 'Deeply caring in love. Best matches: Scorpio, Pisces, Taurus.',
    career: 'Excel in nursing, childcare, real estate, and cooking.',
    health: 'Watch digestive health. Home comforts and family time are healing.',
    today: 'Trust your gut today. Family matters need attention. A nurturing gesture will go a long way.',
  },
  Leo: {
    emoji: '🦁', dates: 'Jul 23 - Aug 22', element: '🔥 Fire', planet: '☀️ Sun',
    stone: '🟡 Ruby', color: '🟠 Gold', lucky: [1,3,10], traits: ['Creative','Generous','Dramatic','Proud','Loyal'],
    desc: 'Leo is the royal sign of the zodiac. Leos are natural leaders who love being in the spotlight.',
    love: 'Romantic and generous in love. Best matches: Aries, Sagittarius, Gemini.',
    career: 'Born performers. Excel in acting, politics, management, and entertainment.',
    health: 'Heart health is important. Regular cardio and creative outlets help.',
    today: 'You are shining today! Lead with confidence. Creative projects get a boost. Romance is in the air!',
  },
  Virgo: {
    emoji: '🌾', dates: 'Aug 23 - Sep 22', element: '🌍 Earth', planet: '☿️ Mercury',
    stone: '💚 Peridot', color: '🟢 Green', lucky: [5,14,23], traits: ['Analytical','Hardworking','Practical','Critical','Helpful'],
    desc: 'Virgo is the sign of service and precision. Virgos are known for their analytical minds and attention to detail.',
    love: 'Devoted and practical in love. Best matches: Taurus, Capricorn, Cancer.',
    career: 'Excel in medicine, research, accounting, and editing.',
    health: 'Digestive system needs care. Clean diet and routine are beneficial.',
    today: 'Your analytical skills are sharp. Tackle complex problems today. Health improvements show results!',
  },
  Libra: {
    emoji: '⚖️', dates: 'Sep 23 - Oct 22', element: '💨 Air', planet: '♀️ Venus',
    stone: '🔵 Opal', color: '🩷 Pink', lucky: [6,15,24], traits: ['Diplomatic','Fair','Social','Indecisive','Charming'],
    desc: 'Libra is the sign of balance and harmony. Libras are natural peacemakers who value justice.',
    love: 'Romantic and balanced in love. Best matches: Gemini, Aquarius, Leo.',
    career: 'Excel in law, diplomacy, design, and counseling.',
    health: 'Kidney health needs attention. Balance work and rest.',
    today: 'Harmony surrounds you today. Partnership opportunities arise. Trust your aesthetic sense!',
  },
  Scorpio: {
    emoji: '🦂', dates: 'Oct 23 - Nov 21', element: '💧 Water', planet: '♇ Pluto',
    stone: '🔴 Topaz', color: '🟤 Maroon', lucky: [8,11,18], traits: ['Passionate','Resourceful','Brave','Secretive','Determined'],
    desc: 'Scorpio is the most intense sign. Scorpios are passionate, determined, and deeply emotional.',
    love: 'Intense and loyal in love. Best matches: Cancer, Pisces, Virgo.',
    career: 'Excel in research, psychology, surgery, and investigation.',
    health: 'Reproductive health needs attention. Deep breathing and yoga help.',
    today: 'Your intuition is powerful today. Secrets may be revealed. Transformation is in progress!',
  },
  Sagittarius: {
    emoji: '🏹', dates: 'Nov 22 - Dec 21', element: '🔥 Fire', planet: '♃ Jupiter',
    stone: '🔵 Turquoise', color: '💙 Blue', lucky: [3,9,21], traits: ['Optimistic','Adventurous','Honest','Restless','Philosophical'],
    desc: 'Sagittarius is the archer of the zodiac. Sagittarians are adventurous, optimistic truth-seekers.',
    love: 'Free-spirited in love. Best matches: Aries, Leo, Aquarius.',
    career: 'Excel in travel, education, philosophy, and entrepreneurship.',
    health: 'Hip and thigh health important. Outdoor activities are beneficial.',
    today: 'Adventure calls! Expand your horizons today. Travel plans get a green light. Lucky financial news!',
  },
  Capricorn: {
    emoji: '🐐', dates: 'Dec 22 - Jan 19', element: '🌍 Earth', planet: '♄ Saturn',
    stone: '🖤 Garnet', color: '⬛ Black', lucky: [4,8,13], traits: ['Ambitious','Disciplined','Patient','Pessimistic','Responsible'],
    desc: 'Capricorn is the most disciplined sign. Capricorns are ambitious, hardworking, and determined.',
    love: 'Committed and serious in love. Best matches: Taurus, Virgo, Scorpio.',
    career: 'Excel in finance, management, engineering, and government.',
    health: 'Bone and joint health important. Yoga and stretching help.',
    today: 'Hard work pays off today! Career advancement is likely. Stay disciplined and focused on goals.',
  },
  Aquarius: {
    emoji: '🏺', dates: 'Jan 20 - Feb 18', element: '💨 Air', planet: '♅ Uranus',
    stone: '💙 Amethyst', color: '🔵 Blue', lucky: [4,7,11], traits: ['Progressive','Original','Independent','Aloof','Humanitarian'],
    desc: 'Aquarius is the visionary of the zodiac. Aquarians are forward-thinking humanitarians.',
    love: 'Intellectual in love. Best matches: Gemini, Libra, Sagittarius.',
    career: 'Excel in technology, science, social work, and innovation.',
    health: 'Circulation needs attention. Group sports and social activities help.',
    today: 'Innovation is your theme today. Think outside the box. Community involvement brings rewards!',
  },
  Pisces: {
    emoji: '🐟', dates: 'Feb 19 - Mar 20', element: '💧 Water', planet: '♆ Neptune',
    stone: '🔵 Aquamarine', color: '🟣 Purple', lucky: [3,7,12], traits: ['Compassionate','Artistic','Intuitive','Escapist','Gentle'],
    desc: 'Pisces is the most spiritual sign. Pisceans are compassionate, artistic, and deeply intuitive.',
    love: 'Romantic and selfless in love. Best matches: Cancer, Scorpio, Taurus.',
    career: 'Excel in art, music, healing, and spirituality.',
    health: 'Immune system needs care. Rest, meditation, and water activities help.',
    today: 'Your creativity flows beautifully today. Spiritual insights are powerful. Trust your dreams!',
  },
};

const getZodiacFromDob = (dobStr) => {
  if (!dobStr) return 'Aries';
  const d = new Date(dobStr + 'T00:00:00');
  const m = d.getMonth() + 1, day = d.getDate();
  if ((m===12&&day>=22)||(m===1&&day<=19)) return 'Capricorn';
  if ((m===1&&day>=20)||(m===2&&day<=18)) return 'Aquarius';
  if ((m===2&&day>=19)||(m===3&&day<=20)) return 'Pisces';
  if ((m===3&&day>=21)||(m===4&&day<=19)) return 'Aries';
  if ((m===4&&day>=20)||(m===5&&day<=20)) return 'Taurus';
  if ((m===5&&day>=21)||(m===6&&day<=20)) return 'Gemini';
  if ((m===6&&day>=21)||(m===7&&day<=22)) return 'Cancer';
  if ((m===7&&day>=23)||(m===8&&day<=22)) return 'Leo';
  if ((m===8&&day>=23)||(m===9&&day<=22)) return 'Virgo';
  if ((m===9&&day>=23)||(m===10&&day<=22)) return 'Libra';
  if ((m===10&&day>=23)||(m===11&&day<=21)) return 'Scorpio';
  if ((m===11&&day>=22)||(m===12&&day<=21)) return 'Sagittarius';
  return 'Capricorn';
};

export default function Zodiac() {
  const [userDob, setUserDob] = useState(localStorage.getItem('userDob') || '');
  const [sign, setSign] = useState('');
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('today');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const username = localStorage.getItem('username');
    if (token && username) {
      fetch(`/api/profile/${username}/`)
        .then(r => r.json())
        .then(d => {
          if (d.date_of_birth) {
            setUserDob(d.date_of_birth);
            localStorage.setItem('userDob', d.date_of_birth);
            const s = getZodiacFromDob(d.date_of_birth);
            setSign(s);
            setData(ZODIAC_DATA[s]);
          }
        }).catch(() => {});
    } else if (userDob) {
      const s = getZodiacFromDob(userDob);
      setSign(s);
      setData(ZODIAC_DATA[s]);
    }
  }, []);

  const handleDobChange = (val) => {
    setUserDob(val);
    const s = getZodiacFromDob(val);
    setSign(s);
    setData(ZODIAC_DATA[s]);
  };

  return (
    <div style={S.page}>
      {/* Hero */}
      <div style={S.hero}>
        <div style={{ fontSize: '60px', marginBottom: '8px' }}>{data?.emoji || '⭐'}</div>
        <h1 style={S.heroTitle}>{sign || 'Your Zodiac'}</h1>
        {data && <p style={S.heroDates}>{data.dates}</p>}
        {data && (
          <div style={S.heroStats}>
            <span>{data.element}</span>
            <span>·</span>
            <span>{data.planet}</span>
            <span>·</span>
            <span>{data.stone}</span>
          </div>
        )}
      </div>

      {/* DOB input if no profile */}
      {!userDob && (
        <div style={{ padding: '16px 20px', backgroundColor: '#f3e5f5', margin: '16px 20px', borderRadius: '12px' }}>
          <p style={{ color: '#6200ea', fontWeight: 'bold', marginBottom: '8px', fontSize: '13px' }}>{t('zd_dob')}</p>
          <input type="date" onChange={e => handleDobChange(e.target.value)}
            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '2px solid #6200ea', fontSize: '14px', boxSizing: 'border-box' }} />
        </div>
      )}

      {data && (
        <div style={{ padding: '0 20px 30px' }}>
          {/* Tabs */}
          <div style={S.tabs}>
            {['today', 'love', 'career', 'health', 'traits'].map(t => (
              <button key={t} onClick={() => setTab(t)} style={{ ...S.tab, ...(tab === t ? S.tabActive : {}) }}>
                {t === 'today' ? '🌟 Today' : t === 'love' ? '❤️ Love' : t === 'career' ? '💼 Career' : t === 'health' ? '💪 Health' : '✨ Traits'}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div style={S.card}>
            {tab === 'today' && (
              <div>
                <h3 style={S.cardTitle}>{t('zd_today')}</h3>
                <p style={S.cardText}>{data.today}</p>
                <div style={S.luckyBox}>
                  <div style={S.luckyTitle}>{t('zd_lucky')}</div>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '8px' }}>
                    {data.lucky.map(n => (
                      <div key={n} style={S.luckyNum}>{n}</div>
                    ))}
                  </div>
                </div>
                <p style={S.cardText}>{data.desc}</p>
              </div>
            )}
            {tab === 'love' && (
              <div>
                <h3 style={S.cardTitle}>{t('zd_love')}</h3>
                <p style={S.cardText}>{data.love}</p>
              </div>
            )}
            {tab === 'career' && (
              <div>
                <h3 style={S.cardTitle}>{t('zd_career')}</h3>
                <p style={S.cardText}>{data.career}</p>
              </div>
            )}
            {tab === 'health' && (
              <div>
                <h3 style={S.cardTitle}>{t('zd_health')}</h3>
                <p style={S.cardText}>{data.health}</p>
              </div>
            )}
            {tab === 'traits' && (
              <div>
                <h3 style={S.cardTitle}>{t('zd_traits')}</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                  {data.traits.map(t => (
                    <span key={t} style={S.trait}>{t}</span>
                  ))}
                </div>
                <div style={{ marginTop: '16px', backgroundColor: '#f3e5f5', borderRadius: '10px', padding: '12px' }}>
                  <div style={{ fontWeight: 'bold', color: '#6200ea', marginBottom: '6px' }}>Lucky Color: {data.color}</div>
                  <div style={{ fontWeight: 'bold', color: '#6200ea' }}>Birthstone: {data.stone}</div>
                </div>
              </div>
            )}
          </div>

          {/* All signs */}
          <h3 style={{ color: '#6200ea', margin: '20px 0 12px' }}>{t('zd_all')}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '8px' }}>
            {Object.entries(ZODIAC_DATA).map(([s, d]) => (
              <div key={s} onClick={() => { setSign(s); setData(ZODIAC_DATA[s]); setTab('today'); window.scrollTo(0,0); }}
                style={{ backgroundColor: sign===s ? '#6200ea' : 'white', borderRadius: '10px', padding: '10px 6px', textAlign: 'center', cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }}>
                <div style={{ fontSize: '20px' }}>{d.emoji}</div>
                <div style={{ fontSize: '10px', color: sign===s ? 'white' : '#666', marginTop: '2px' }}>{s}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  page: { maxWidth: '700px', margin: '0 auto', paddingBottom: '30px' },
  hero: { background: 'linear-gradient(135deg, #1a1a2e, #16213e, #6200ea)', padding: '30px 20px', textAlign: 'center', color: 'white' },
  heroTitle: { fontSize: '32px', fontWeight: '900', margin: '0 0 4px', color: 'white' },
  heroDates: { color: 'rgba(255,255,255,0.7)', fontSize: '14px', margin: '0 0 12px' },
  heroStats: { display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap', fontSize: '12px', color: 'rgba(255,255,255,0.8)' },
  tabs: { display: 'flex', gap: '6px', flexWrap: 'wrap', margin: '16px 0', justifyContent: 'center' },
  tab: { padding: '6px 12px', borderRadius: '15px', border: '2px solid #6200ea', backgroundColor: 'white', color: '#6200ea', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' },
  tabActive: { backgroundColor: '#6200ea', color: 'white' },
  card: { backgroundColor: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' },
  cardTitle: { color: '#6200ea', margin: '0 0 12px', fontSize: '17px' },
  cardText: { color: '#444', lineHeight: 1.7, fontSize: '14px' },
  luckyBox: { backgroundColor: '#f3e5f5', borderRadius: '10px', padding: '12px', textAlign: 'center', margin: '12px 0' },
  luckyTitle: { color: '#6200ea', fontWeight: 'bold', fontSize: '13px' },
  luckyNum: { backgroundColor: '#6200ea', color: 'white', width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '14px' },
  trait: { backgroundColor: '#f3e5f5', color: '#6200ea', padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '500' },
};
